import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import type {
  OrganizationMember,
  TeamWorkspaceInviteRole,
} from '@cartwhl/common/types';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, userStore } from 'stores/global';

function workspaceRoleFromMember(
  m: OrganizationMember,
): TeamWorkspaceInviteRole {
  if (m.isOrgAdminOnly) return 'admin_only';
  if (m.accessLevel === 'EDIT' || m.accessLevel === 'INVITED_EDIT') {
    return 'admin_member';
  }
  return 'member';
}

function initialWorkspaceRoleForModal(
  m: OrganizationMember | undefined,
  ownerUserId: string | undefined,
): TeamWorkspaceInviteRole {
  if (!m) return 'member';
  const base = workspaceRoleFromMember(m);
  if (ownerUserId && m.userID === ownerUserId && base === 'member') {
    return 'admin_member';
  }
  return base;
}

const ROLE_OPTIONS: {
  role: TeamWorkspaceInviteRole;
  label: string;
  description: string;
}[] = [
  {
    role: 'admin_member',
    label: 'Admin & Member',
    description:
      'Full team admin controls and uses a paid seat (same as a typical admin).',
  },
  {
    role: 'member',
    label: 'Member',
    description:
      'Uses a seat; can work in the workspace without admin controls.',
  },
  {
    role: 'admin_only',
    label: 'Admin',
    description: 'Team admin only—no paid seat. Cannot access apps or canvas.',
  },
];

export default function AssignOrEditTeamSeatModal({
  close,
  member,
}: {
  close: () => void;
  member?: OrganizationMember;
}) {
  const { organizationInfo } = useSnapshot(userStore.state);
  const hasEditAccess = organizationInfo?.accessLevel === 'EDIT';
  const isEditMode = Boolean(member);
  const ownerUserId = organizationInfo?.createdBy;
  const isOrgOwner = Boolean(
    member && ownerUserId && member.userID === ownerUserId,
  );

  const [email, setEmail] = useState(member?.email ?? '');
  const [workspaceRole, setWorkspaceRole] = useState<TeamWorkspaceInviteRole>(
    () => initialWorkspaceRoleForModal(member, ownerUserId),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (
      member &&
      organizationInfo?.createdBy &&
      member.userID === organizationInfo.createdBy &&
      workspaceRole === 'member'
    ) {
      setWorkspaceRole('admin_member');
    }
  }, [member, organizationInfo?.createdBy, workspaceRole]);

  function handleOpenChange(open: boolean) {
    if (!open) close();
  }

  const handleSubmit = async () => {
    if (!email) return;
    if (isOrgOwner && workspaceRole === 'member') {
      noticesStore.error(
        'The organization owner must remain a team admin with a seat or admin-only access.',
      );
      return;
    }
    setSubmitting(true);
    const target = member?.userID ?? email;
    const ok = await userStore.assignSeatToUser(target, 'team', {
      workspaceRole,
    });
    setSubmitting(false);
    if (ok) {
      noticesStore.success(isEditMode ? 'Member updated' : 'Invite sent');
      close();
    }
  };

  return (
    <Modal
      title={isEditMode ? 'Edit team member' : 'Invite to team'}
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text as="label" variant="studio/body/sm">
            User email
          </Text>
          <Input
            name="assign-team-seat-email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isEditMode}
          />
        </div>

        {hasEditAccess ? (
          <div className="flex flex-col gap-2">
            <Text as="span" variant="studio/body/sm">
              Role
            </Text>
            <div className="flex flex-col gap-2">
              {ROLE_OPTIONS.map(({ role, label, description }) => {
                const selected = workspaceRole === role;
                const memberOnlyBlocked = isOrgOwner && role === 'member';
                return (
                  <Button
                    key={role}
                    type="button"
                    variant={selected ? 'primary' : 'secondary'}
                    size="sm"
                    disabled={memberOnlyBlocked}
                    title={
                      memberOnlyBlocked
                        ? 'Organization owners must keep admin access.'
                        : undefined
                    }
                    className={`h-auto justify-start py-2.5 active:scale-100${selected ? ' border border-transparent' : ''}`}
                    onClick={() => {
                      if (!memberOnlyBlocked) setWorkspaceRole(role);
                    }}
                  >
                    <span className="flex w-full flex-col items-start gap-0.5 text-left">
                      <span>{label}</span>
                      <Text
                        as="span"
                        variant="studio/body/sm"
                        className="font-normal"
                      >
                        {description}
                      </Text>
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-2 flex flex-row flex-nowrap">
          <Button variant="secondary" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            className="ml-auto"
            variant="primary"
            onClick={handleSubmit}
            disabled={
              submitting || !email || (isOrgOwner && workspaceRole === 'member')
            }
          >
            {isEditMode ? 'Save changes' : 'Send invite'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
